import { Instant, LocalDateTime, LocalTime, ZoneId } from "@js-joda/core";
import * as React from "react";
import * as Ons from "react-onsenui";
import { ScheduledVisitEditRequestParams } from "../../../messages/ScheduledVisitEditRequest";
import { STRINGS } from "../../../strings";
import { globalize, translate } from "../../../translate";
import { BackButton } from "../../BackButton";

export type ScheduledVisitRescheduleScreenErrors =
  | ScheduledVisitRescheduleScreenErrors.NoError
  | ScheduledVisitRescheduleScreenErrors.ShowError;

export declare namespace ScheduledVisitRescheduleScreenErrors {
  export interface NoError {
    type: "Empty";
  }

  export interface ShowError {
    type: "ShowError";
    errorMessage: string;
  }
}

export interface ScheduledVisitRescheduleScreenProps {
  initialStartTime: LocalDateTime;
  initialEndTime: LocalDateTime;
  isCancel: boolean;
  onBackClick: () => void;
  onSubmitClick: (params: ScheduledVisitEditRequestParams) => void;
}

export interface ScheduledVisitRescheduleScreenState {
  ScheduledVisitRescheduleScreenErrors: ScheduledVisitRescheduleScreenErrors;
  requestedStartTime: LocalDateTime | null;
  requestedEndTime: LocalDateTime | null;
  requestMessage: string;
}

export class ScheduledVisitRescheduleScreen extends React.Component<
  ScheduledVisitRescheduleScreenProps,
  ScheduledVisitRescheduleScreenState
> {
  constructor(props: ScheduledVisitRescheduleScreenProps) {
    super(props);
    this.state = {
      ScheduledVisitRescheduleScreenErrors: { type: "Empty" },
      requestedStartTime: null,
      requestedEndTime: null,
      requestMessage: "",
    };
  }

  handleSubmitClick() {
    this.setState((_prevState: ScheduledVisitRescheduleScreenState) => {
      return {
        ScheduledVisitRescheduleScreenErrors: {
          type: "Empty",
        },
      };
    });

    if (this.props.isCancel) {
      this.props.onSubmitClick({
        type: "CANCEL",
        message: this.state.requestMessage,
      });
    } else if (
      (this.state.requestedStartTime === null && this.state.requestedEndTime === null) ||
      (this.state.requestedStartTime === this.props.initialStartTime &&
        this.state.requestedEndTime === this.props.initialEndTime)
    ) {
      this.setState((_prevState: ScheduledVisitRescheduleScreenState) => {
        return {
          ScheduledVisitRescheduleScreenErrors: {
            type: "ShowError",
            errorMessage: translate(STRINGS.NO_CHANGES_WERE_MADE),
          },
        };
      });
    } else {
      const now = Instant.now();
      const startTimeDate =
        this.state.requestedStartTime === null
          ? this.props.initialStartTime
          : this.state.requestedStartTime;
      const endTimeDate =
        this.state.requestedEndTime === null
          ? this.props.initialEndTime
          : this.state.requestedEndTime;

      if (
        !startTimeDate.isBefore(endTimeDate) ||
        LocalDateTime.ofInstant(now, ZoneId.SYSTEM).isAfter(startTimeDate)
      ) {
        this.setState((_prevState: ScheduledVisitRescheduleScreenState) => {
          return {
            ScheduledVisitRescheduleScreenErrors: {
              type: "ShowError",
              errorMessage: translate(STRINGS.PLEASE_PICK_VALID_DATES),
            },
          };
        });
      } else {
        this.props.onSubmitClick({
          type: "RESCHEDULE",
          startTime: startTimeDate,
          endTime: endTimeDate,
          message: this.state.requestMessage,
        });
      }
    }
  }

  handleStartTimeChange(value: string) {
    if (value === "") {
      return;
    }

    const dateTimeValue = localDateTimeFromHtmlInputValue(value);

    this.setState((_prevState: ScheduledVisitRescheduleScreenState) => {
      return {
        requestedStartTime: dateTimeValue,
      };
    });
  }

  handleEndTimeChange(value: string) {
    if (value === "") {
      return;
    }

    const dateTimeValue = localDateTimeFromHtmlInputValue(value);

    this.setState((_prevState: ScheduledVisitRescheduleScreenState) => {
      return {
        requestedEndTime: dateTimeValue,
      };
    });
  }

  handleMessageChange(value: string) {
    this.setState((_prevState: ScheduledVisitRescheduleScreenState) => {
      return {
        requestMessage: value,
      };
    });
  }

  render() {
    return (
      <div className="ScheduledVisitRescheduleScreen__root">
        <div className="ScheduledVisitRescheduleScreen__header">
          <BackButton onClick={() => this.props.onBackClick()} />
          <div className="ScheduledVisitRescheduleScreen__headerTitle">
            {this.props.isCancel ? "Cancel" : "Reschedule"}{" "}
            {translate(STRINGS.REQUEST_FOR_APPOINTMENT)}:
            <br />
            {globalize().formatLocalDateTime(this.props.initialStartTime, { date: "long" })}
            <br />
            {globalize().formatLocalDateTime(this.props.initialStartTime, { time: "short" })} -{" "}
            {globalize().formatLocalDateTime(this.props.initialEndTime, { time: "short" })}
          </div>
        </div>
        <div className="ScheduledVisitRescheduleScreen__body">
          {this.props.isCancel ? (
            ""
          ) : (
            <div className="ScheduledVisitRescheduleScreen__card">
              <div className="ScheduledVisitRescheduleScreen__cardHeader">
                {translate(STRINGS.CHANGE_VISIT_TIMES)}
              </div>
              <div className="ScheduledVisitRescheduleScreen__cardTimeRow">
                <div className="ScheduledVisitRescheduleScreen__cardTimeRowHeader">
                  {translate(STRINGS.START_TIME)}:
                </div>
                <div className="ScheduledVisitRescheduleScreen__cardTimeRowContent">
                  <input
                    defaultValue={localDateTimeToHtmlInputValue(
                      this.state.requestedStartTime !== null
                        ? this.state.requestedStartTime
                        : this.props.initialStartTime
                    )}
                    type="datetime-local"
                    className={
                      "ScheduledVisitRescheduleScreen__cardInput " +
                      (this.state.ScheduledVisitRescheduleScreenErrors.type === "Empty"
                        ? ""
                        : "ScheduledVisitRescheduleScreen__dateInputError")
                    }
                    onChange={(e) => this.handleStartTimeChange(e.target.value)}
                  />
                </div>
              </div>
              <div className="ScheduledVisitRescheduleScreen__cardTimeRow">
                <div className="ScheduledVisitRescheduleScreen__cardTimeRowHeader">
                  {translate(STRINGS.END_TIME)}:
                </div>
                <div className="ScheduledVisitRescheduleScreen__cardTimeRowContent">
                  <input
                    defaultValue={localDateTimeToHtmlInputValue(
                      this.state.requestedEndTime !== null
                        ? this.state.requestedEndTime
                        : this.props.initialEndTime
                    )}
                    type="datetime-local"
                    className={
                      "ScheduledVisitRescheduleScreen__cardInput " +
                      (this.state.ScheduledVisitRescheduleScreenErrors.type === "Empty"
                        ? ""
                        : "ScheduledVisitRescheduleScreen__dateInputError")
                    }
                    onChange={(e) => this.handleEndTimeChange(e.target.value)}
                  />
                </div>
              </div>
              {this.state.ScheduledVisitRescheduleScreenErrors.type === "Empty" ? (
                ""
              ) : (
                <div className="ScheduledVisitRescheduleScreen__cardTimeRow ScheduledVisitRescheduleScreen__cardError">
                  {this.state.ScheduledVisitRescheduleScreenErrors.errorMessage}
                </div>
              )}
            </div>
          )}
          <div className="ScheduledVisitRescheduleScreen__card">
            <div className="ScheduledVisitRescheduleScreen__cardHeader">
              {translate(STRINGS.MESSAGE_FOR_THE_COORDINATOR)}:
            </div>
            <textarea
              placeholder="E.G: I feel under the weather today..."
              className="ScheduledVisitRescheduleScreen__text"
              onChange={(e) => this.handleMessageChange(e.target.value)}
            />
          </div>
          <Ons.Button
            className="ScheduledVisitRescheduleScreen__submit"
            onClick={() => this.handleSubmitClick()}
          >
            {translate(STRINGS.REQUEST_TO)}{" "}
            {this.props.isCancel ? translate(STRINGS.CANCEL) : translate(STRINGS.RESCHEDULE)}
          </Ons.Button>
        </div>
      </div>
    );
  }
}

/**
 * Format a [[LocalDateTime]] to a string that is suitable for a <input type="datetime-local"> element
 *
 * <https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/datetime-local>
 */
export function localDateTimeToHtmlInputValue(localDateTime: LocalDateTime): string {
  // HTML "datetime-local" does not support seconds so we trim them off
  return localDateTime.toString().substr(0, 16);
}

/**
 * Parse a string value from an <input type="datetime-local"> element
 *
 * <https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/datetime-local>
 */
export function localDateTimeFromHtmlInputValue(value: string): LocalDateTime {
  return LocalDateTime.parse(value);
}

/**
 * Parse a string value from an <input type="datetime-local"> element
 *
 * <https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/datetime-local>
 */
export function localTimeFromHtmlInputValue(value: string): LocalTime {
  return LocalTime.parse(value);
}
